import React, { Component } from 'react';
import { Modal } from 'react-bootstrap';
import Alert from 'sweetalert-react';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import * as Http from '../../../../http';
import { t, onLangChange } from '../../../../i18n';
import Loading from '../../../../components/Loading';
import '../../CapMrCor/cap-mr-cor.style.scss';
import '../../CapMrCor/raroc-theme.style.scss';

const fmtDate = (d) => (d ? String(d).replace('T', ' ').slice(0, 19) : '—');
const day = (d) => String(d || '').slice(0, 10);
const COMPONENT_CAP_MR_COR = 'cap-mr-cor';
const COMPONENT_RAROC = 'raroc';
const COMPONENT_RAROC_CR = 'raroc-cr';
const CAP_PREFIX = 'STG_MR_COR_CAP_';
const CR_PREFIX = 'RAROC_CR_';
const DATASET_PREFIX = 'RAROC_';

const ensureDatasetPrefix = (raw = '') => {
    const clean = String(raw || '').trim();
    if (!clean) return '';
    if (clean.toUpperCase().startsWith(DATASET_PREFIX)) return clean;
    return `${DATASET_PREFIX}${clean}`;
};

const datasetSuffix = (fileName = '') => {
    if (fileName.startsWith(CAP_PREFIX)) return fileName.slice(CAP_PREFIX.length).replace(/^RAROC_/i, '');
    if (fileName.startsWith(CR_PREFIX)) return fileName.slice(CR_PREFIX.length).replace(/^RAROC_/i, '');
    return String(fileName || '').replace(/^RAROC_/i, '');
};

const normalizeItems = (rows = [], component) =>
    rows
        .map((row) => ({
            ...row,
            component: row.component || component,
            has_result: typeof row.has_result === 'boolean' ? row.has_result : String(row.status || '').toUpperCase() === 'CALCULATED',
        }))
        .filter((row) => row.has_result);

const isRarocCrItem = (row = {}) => {
    const name = String(row.file_name || '').toUpperCase();
    const comp = String(row.component || '').toLowerCase();
    return comp === COMPONENT_RAROC_CR || name.startsWith('RAROC_CR_');
};

const csvEscape = (value) => {
    if (value === null || value === undefined) return '';
    const s = String(value);
    if (!/[",\n\r]/.test(s)) return s;
    return `"${s.replace(/"/g, '""')}"`;
};

const rowsToCsv = (rows = []) => {
    if (!rows.length) return '';
    const columns = [];
    rows.forEach((row) => {
        Object.keys(row || {}).forEach((key) => {
            if (!columns.includes(key)) columns.push(key);
        });
    });
    const head = columns.map(csvEscape).join(',');
    const body = rows.map((row) => columns.map((col) => csvEscape(row && row[col] != null ? row[col] : '')).join(',')).join('\n');
    return `${head}\n${body}`;
};

const downloadCsv = (fileName, rows = []) => {
    const csv = rowsToCsv(rows);
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const rowsToXlsx = async (rows = []) => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Sheet1');

    if (!rows.length) return null;

    const columns = [];
    rows.forEach((row) => {
        Object.keys(row || {}).forEach((key) => {
            if (!columns.includes(key)) columns.push(key);
        });
    });

    sheet.columns = columns.map((col) => ({
        header: col,
        key: col,
        width: Math.max(10, Math.min(30, String(col).length + 2)),
    }));

    sheet.getRow(1).font = { bold: true };
    sheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5D24E' } };

    rows.forEach((row) => {
        sheet.addRow(row);
    });

    return workbook;
};

const downloadXlsx = async (fileName, rows = []) => {
    try {
        const workbook = await rowsToXlsx(rows);
        if (!workbook) return;
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, fileName);
    } catch (error) {
        console.error('Error creating XLSX:', error);
    }
};

const capRowsToCsv = (capData = {}) => {
    const rows = [];
    if (capData.total && typeof capData.total === 'object') rows.push(capData.total);
    if (Array.isArray(capData.rows)) rows.push(...capData.rows);
    return rows;
};

const buildCsvFiles = (datasetName, capData, crData) => {
    const files = [];
    const safeDataset = ensureDatasetPrefix(datasetName) || DATASET_PREFIX.slice(0, -1);

    const capRows = capRowsToCsv(capData);
    if (capRows.length) {
        files.push({
            report_name: `${safeDataset}_STG_MR_COR_CAP.xlsx`,
            csvRows: capRows,
            isGeneratedCsv: true,
        });
    }

    const output = crData && crData.output && typeof crData.output === 'object' ? crData.output : {};
    Object.keys(output).forEach((sheetName) => {
        const rows = Array.isArray(output[sheetName]) ? output[sheetName] : [];
        files.push({
            report_name: `${safeDataset}_${sheetName}.xlsx`,
            csvRows: rows,
            isGeneratedCsv: true,
        });
    });

    return files.sort((a, b) => String(a.report_name).localeCompare(String(b.report_name)));
};

const mergeByDataset = (capItems = [], crItems = []) => {
    const grouped = {};
    [...capItems, ...crItems].forEach((row) => {
        const key = `${day(row.report_date)}__${datasetSuffix(row.file_name || '')}`;
        if (!grouped[key]) {
            grouped[key] = {
                key,
                report_date: row.report_date,
                dataset: ensureDatasetPrefix(datasetSuffix(row.file_name || '')),
                upload_time: row.upload_time,
                approval_time: row.approval_time,
                upload_by: row.upload_by,
                approval_by: row.approval_by,
                status: row.status,
                capItem: null,
                crItem: null,
            };
        }
        if (row.component === COMPONENT_RAROC_CR) grouped[key].crItem = row;
        else grouped[key].capItem = row;

        if (new Date(row.upload_time || 0).getTime() > new Date(grouped[key].upload_time || 0).getTime()) {
            grouped[key].upload_time = row.upload_time;
            grouped[key].upload_by = row.upload_by;
            grouped[key].approval_by = row.approval_by;
            grouped[key].approval_time = row.approval_time;
            grouped[key].status = row.status;
        }
    });
    return Object.values(grouped).sort((a, b) => new Date(b.upload_time || 0).getTime() - new Date(a.upload_time || 0).getTime());
};

// STG_MR & COR CAP — Reports: danh sách batch đã tính → xem bảng kết quả.
export default class ReportList extends Component {
    state = {
        items: [],
        loading: true,
        searchField: 'report_name',
        query: '',
        applied: { field: 'report_name', q: '' },
        activeDataset: null,
        reportFiles: [],
        reportFilesLoading: false,
        showReportModal: false,
        showFileDownloadError: false,
        failedDownloadsList: [],
        _lang: null,
    };

    componentDidMount() {
        this._unsub = onLangChange(() => this.setState({ _lang: Date.now() }));
        this.load();
    }
    componentWillUnmount() {
        this._unsub && this._unsub();
    }

    load = () => {
        this.setState({ loading: true });
        Promise.all([Http.getCapMrCorList(), Http.getUploadManagement(COMPONENT_RAROC)])
            .then(([capRes, crRes]) => {
                const capItems = capRes && capRes.Success ? normalizeItems(capRes.Data || [], COMPONENT_CAP_MR_COR) : [];
                const crItems =
                    crRes && crRes.Success
                        ? normalizeItems((crRes.Data || []).filter(isRarocCrItem), COMPONENT_RAROC_CR).map((row) => ({
                              ...row,
                              component: COMPONENT_RAROC_CR,
                          }))
                        : [];
                const items = mergeByDataset(capItems, crItems);
                this.setState({ items, loading: false });
            })
            .catch(() => this.setState({ loading: false }));
    };

    onSearch = () => this.setState({ applied: { field: this.state.searchField, q: this.state.query.trim().toLowerCase() } });

    filtered() {
        const { items, applied } = this.state;
        if (!applied.q) return items;
        return items.filter((it) => {
            const val = applied.field === 'report_date' ? day(it.report_date) : String(it.dataset || '');
            return val.toLowerCase().includes(applied.q);
        });
    }

    onDeleteDataset = (item) => {
        if (!item) return;
        if (!window.confirm(t('rstg.fm.confirmPre') + item.dataset + t('rstg.fm.confirmPost'))) return;

        this.setState({ loading: true });
        const reqs = [];
        if (item.capItem) reqs.push(Http.deleteCapMrCor(day(item.capItem.report_date), item.capItem.file_name));
        if (item.crItem) {
            reqs.push(
                Http.deleteListReport(COMPONENT_RAROC, {
                    report_date: day(item.crItem.report_date),
                    file_name: item.crItem.file_name,
                }),
            );
        }

        Promise.all(reqs)
            .then(() => this.load())
            .catch(() => this.setState({ loading: false }));
    };

    onSelect = (item) => {
        this.setState({
            activeDataset: item,
            reportFiles: [],
            reportFilesLoading: true,
            showReportModal: true,
        });

        const fetchCapResultSafe = () => {
            // RAROC là MỘT dataset: CAP + CR chung report_name nên kết quả nằm cùng 1 dòng.
            // crItem có thể null sau khi đồng nhất → fallback sang capItem để vẫn lấy được kết quả.
            const capSrc = item.capItem || item.crItem || null;
            if (!capSrc) return Promise.resolve(null);
            return Http.getCapMrCorResult(day(capSrc.report_date), capSrc.file_name)
                .then((res) => (res && res.Success && Array.isArray(res.Data) && res.Data[0] ? res.Data[0].data : null))
                .catch(() => null);
        };

        const fetchCrResultSafe = () => {
            const crSrc = item.crItem || item.capItem || null;
            if (!crSrc) return Promise.resolve(null);
            return Http.getRarocCrResult(day(crSrc.report_date), crSrc.file_name)
                .then((res) => (res && res.Success && Array.isArray(res.Data) && res.Data[0] ? res.Data[0].data : null))
                .catch(() => null);
        };

        Promise.all([fetchCapResultSafe(), fetchCrResultSafe()])
            .then(([capData, crData]) => {
                const files = buildCsvFiles(item.dataset, capData, crData);
                this.setState({ reportFilesLoading: false, reportFiles: files });
            })
            .catch(() => this.setState({ reportFilesLoading: false }));
    };

    closeModal = () =>
        this.setState({
            showReportModal: false,
            activeDataset: null,
            reportFiles: [],
            reportFilesLoading: false,
        });

    onDownloadOne = (fileItem) => {
        if (!this.state.activeDataset || !fileItem) return;
        if (fileItem.isGeneratedCsv) {
            downloadXlsx(fileItem.report_name, fileItem.csvRows || []);
            return;
        }
        Http.downloadFile(fileItem.report_name, this.state.activeDataset.report_date, fileItem.sourceFileName, null, fileItem.sourceComponent);
    };

    onSaveAllFiles = () => {
        const { activeDataset, reportFiles } = this.state;
        if (!activeDataset || !reportFiles.length) return;

        const csvFiles = reportFiles.filter((f) => f.isGeneratedCsv);
        if (csvFiles.length) {
            csvFiles.forEach((fileItem) => this.onDownloadOne(fileItem));
            return;
        }

        const fileArr = reportFiles.map((f) => Http.makeDownloadFileInfo(f.report_name, activeDataset.report_date, f.sourceFileName, null, f.sourceComponent));
        Http.downloadAllFiles(fileArr, activeDataset.report_date, activeDataset.dataset).then((result) => {
            if (result.is_error) {
                this.setState({
                    showFileDownloadError: true,
                    failedDownloadsList: result.failed_files,
                });
            }
        });
    };

    render() {
        const { loading, activeDataset, reportFiles, reportFilesLoading, showReportModal, searchField, query } = this.state;
        const items = this.filtered();
        return (
            <div className='content'>
                <div className='container-fluid rstg'>
                    {loading && <Loading />}

                    <div className='rstg-title'>{t('rstg.rp.title')}</div>

                    <div className='rstg-card'>
                        <div className='rstg-card-head'>
                            <h4>
                                {t('rstg.rp.listTitle')} <span className='rstg-count'>({items.length})</span>
                            </h4>
                            <button className='rstg-btn' onClick={this.load}>
                                <i className='pe-7s-refresh-2' /> {t('rstg.common.refresh')}
                            </button>
                        </div>

                        <div className='rstg-filter'>
                            <select className='rstg-select' value={searchField} onChange={(e) => this.setState({ searchField: e.target.value })}>
                                <option value='report_name'>{t('rstg.col.reportName')}</option>
                                <option value='report_date'>{t('rstg.col.reportDate')}</option>
                            </select>
                            <input className='rstg-search-input' value={query} onChange={(e) => this.setState({ query: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && this.onSearch()} />
                            <button className='rstg-search-btn' onClick={this.onSearch}>
                                {t('rstg.btn.search')}
                            </button>
                        </div>

                        <div className='rstg-table-wrap'>
                            <table className='rstg-table'>
                                <thead>
                                    <tr>
                                        <th>{t('rstg.col.reportDate')}</th>
                                        <th>{t('rstg.col.reportName')}</th>
                                        <th>{t('rstg.col.approver')}</th>
                                        <th>{t('rstg.col.approvalTime')}</th>
                                        <th>{t('rstg.col.uploader')}</th>
                                        <th>{t('rstg.col.uploadTime')}</th>
                                        <th>{t('rstg.col.status')}</th>
                                        <th style={{ width: 260 }}>{t('rstg.col.action')}</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.length === 0 && (
                                        <tr>
                                            <td colSpan='9'>
                                                <div className='rstg-empty'>{t('rstg.rp.empty')}</div>
                                            </td>
                                        </tr>
                                    )}
                                    {items.map((it) => (
                                        <tr key={it.key}>
                                            <td>{day(it.report_date)}</td>
                                            <td>
                                                <span className='rstg-link' onClick={() => this.onSelect(it)}>
                                                    {it.dataset}
                                                </span>
                                            </td>
                                            <td>{it.approval_by || '—'}</td>
                                            <td>{fmtDate(it.approval_time)}</td>
                                            <td>{it.upload_by || '—'}</td>
                                            <td>{fmtDate(it.upload_time)}</td>
                                            <td>{it.status || '—'}</td>
                                            <td>
                                                <div className='rstg-row-actions'>
                                                    <button className='rstg-btn rstg-btn--navy' onClick={() => this.onSelect(it)}>
                                                        <i className='pe-7s-look' /> {t('rstg.btn.view')}
                                                    </button>
                                                </div>
                                            </td>
                                            <td>
                                                <i className='pe-7s-trash rstg-del' title={t('rstg.btn.delete')} onClick={() => this.onDeleteDataset(it)} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <Modal show={showReportModal} onHide={this.closeModal}>
                        <Modal.Header closeButton>
                            <Modal.Title>{activeDataset ? activeDataset.dataset : 'Report files'}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {reportFilesLoading ? (
                                <div style={{ height: 60 }}>
                                    <Loading size='small' />
                                </div>
                            ) : reportFiles.length ? (
                                <div>
                                    <div className='report-detail-list'>
                                        {reportFiles.map((item) => (
                                            <div className='report-detail-item' key={item.report_name}>
                                                <span className='report-detail-name'>{item.report_name}</span>
                                                <div className='report-detail-actions'>
                                                    <button className='report-dl-btn report-dl-xlsx' onClick={() => this.onDownloadOne(item)}>
                                                        <i className='pe-7s-download' /> XLSX
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className='report-detail-footer'>
                                        <button type='button' className='btn btn-primary report-save-all-btn' onClick={this.onSaveAllFiles}>
                                            <i className='pe-7s-download' /> SAVE ALL XLSX FILES
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className='rstg-alert rstg-alert--warn'>
                                    <i className='pe-7s-info' /> No downloadable report files found.
                                </div>
                            )}
                        </Modal.Body>
                    </Modal>

                    <Alert
                        title='Some Files Failed to Download!'
                        text={`Please check the following files: ${this.state.failedDownloadsList.join(', ')}`}
                        show={this.state.showFileDownloadError}
                        onConfirm={() => {
                            this.setState({ showFileDownloadError: false });
                        }}
                    />
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// src/pages/Raroc/Report/Reports/ReportList.js